import React, { useRef } from 'react';
import {
  TableRow,
  TableCell,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { getFolderName } from '../../../../utils/getFolderName';
import { FileActionsMenu } from '../../modules/FileActionsMenu';
import { InlineRenameField, hasAction, useInlineRename } from '../../modules/InlineRename';
import { AccessMark, FolderGlyph, useFolderLinkText } from '../../modules/FolderGlyph';

// `link`: how Orbit manages the folder (getFolderLink), shown in its icon and type
export const FolderRow = ({ folder, link, selected, onClick, dragData, isOver: isOverProp, canDrop, checkable = false, hasActions = false, actions, onAction, onRename }) => {
  const { t } = useTranslation(['files', 'common']);
  const linkText = useFolderLinkText(link);
  const rowRef = useRef(null);
  const { renaming, stopRenaming, nameProps, rowKeyDown } = useInlineRename({
    enabled: Boolean(onRename) && hasAction(actions, 'rename'),
    onOpen: () => onClick(folder),
  });

  const dropId = `folder:${folder.folder_id}`;

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable(
    dragData ?? { id: `drag-disabled-${folder.folder_id}`, disabled: true }
  );

  const { setNodeRef: setDropRef, isOver: isOverDrop } = useDroppable({
    id: dropId,
    // A folder its access rules leave read-only takes no drops
    disabled: !canDrop || folder.can_write === false,
  });

  const isOver = isOverProp || isOverDrop;
  // dnd-kit marks a disabled draggable aria-disabled, which would announce the folder as unusable
  const draggable = Boolean(dragData && !dragData.disabled);

  const setRef = (el) => {
    setDragRef(el);
    setDropRef(el);
    rowRef.current = el;
  };

  return (
    <TableRow
      ref={setRef}
      hover
      selected={selected}
      {...(draggable ? { ...listeners, ...attributes } : {})}
      tabIndex={0}
      sx={{
        cursor: isDragging ? 'grabbing' : (draggable ? 'grab' : 'pointer'),
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
        opacity: isDragging ? 0.4 : 1,
        outline: isOver ? '2px solid' : 'none',
        outlineColor: 'primary.main',
        backgroundColor: isOver ? 'action.selected' : undefined,
        '&:hover': { backgroundColor: isOver ? 'action.selected' : 'action.hover' },
      }}
      // Folders open on a single click; ignore the second click of a habitual
      // double-click, which would otherwise open whatever lands under the cursor next
      onClick={(e) => { if (e.detail <= 1) onClick(folder); }}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget || rowKeyDown(e)) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(folder);
        }
      }}
    >
      {/* Folders can't be checked; the cell keeps the columns lined up */}
      {checkable && <TableCell padding="checkbox" />}
      <TableCell>
        <Tooltip title={linkText?.hint || ''}>
          <span style={{ display: 'inline-flex', width: 30, justifyContent: 'center' }}>
            <FolderGlyph link={link} size={24} />
          </span>
        </Tooltip>
      </TableCell>
      <TableCell>
        {renaming ? (
          <InlineRenameField
            name={folder.folder_name}
            onSave={(newName) => onRename(folder, newName)}
            onClose={({ keyboard }) => {
              stopRenaming();
              if (keyboard) rowRef.current?.focus();
            }}
            sx={{ maxWidth: 360, '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
          />
        ) : (
          <>
            <Typography
              variant="body2"
              noWrap
              {...nameProps}
              sx={{ display: 'inline-block', maxWidth: 300, verticalAlign: 'middle' }}
            >
              {getFolderName(folder.folder_name)}
            </Typography>
            <AccessMark item={folder} sx={{ ml: 0.75, verticalAlign: 'middle' }} />
          </>
        )}
      </TableCell>
      <TableCell>
        <Tooltip title={linkText?.hint || ''}>
          <Chip label={linkText?.label || t('files:folder_type')} size="small" color="secondary" variant="outlined" />
        </Tooltip>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {t('files:item_count', { count: folder.subfolder_count + folder.file_count })}
        </Typography>
      </TableCell>
      <TableCell>
        {/* Folders don't record who created them */}
        <Typography variant="body2" color="text.disabled">—</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {new Date(folder.created_at).toLocaleDateString()}
        </Typography>
      </TableCell>
      {hasActions && (
        <TableCell align="right" padding="none" sx={{ pr: 1 }}>
          {actions?.length > 0 && <FileActionsMenu file={folder} actions={actions} onAction={onAction} />}
        </TableCell>
      )}
    </TableRow>
  );
};
